import moment from 'moment';
import {
  CurrentQuoteState,
  CurrentQuoteValidationState,
  ItemStatus,
  LineItem,
  ProductData,
} from 'types';
import { QUOTE_ITEM_EXTENDED_WARRANTY_LINE } from 'utils/constants';
import { QuoteStatus } from 'types/QuoteStatus';
import { DEFAULT_DATE_FORMAT_ISO, DEFAULT_DATE_FORMAT_UI } from 'utils/constants/date';
import { defaultCurrentQuoteValidationState } from 'state/defaults';
import { itemsHaveInvalidItemQuantity, hasCurrencyMismatchBetweenQuoteAndAccount } from './quote';

export const isCheckoutAllowed = ({
  quote,
  isExpired,
  isWritable,
  isLockedByAnotherUser,
  hasActiveItems,
  hasCurrencyMismatch,
  hasOutdatedDiscounts,
  hasUnavailableItems,
}: {
  quote: CurrentQuoteState;
  isExpired: boolean;
  isWritable: boolean;
  isLockedByAnotherUser: boolean;
  hasActiveItems: boolean;
  hasCurrencyMismatch: boolean;
  hasOutdatedDiscounts: boolean;
  hasUnavailableItems: boolean;
}) => {
  if (isWritable) {
    return true;
  }

  return (
    quote.commerceComplete &&
    hasActiveItems &&
    !quote.isOrdered &&
    !isExpired &&
    !isLockedByAnotherUser &&
    !hasCurrencyMismatch &&
    !hasOutdatedDiscounts &&
    !hasUnavailableItems
  );
};

export const isQuoteExpired = ({ quote }: { quote: CurrentQuoteState }) => {
  const expirationDate = moment(quote.expirationDate, DEFAULT_DATE_FORMAT_ISO).format(
    DEFAULT_DATE_FORMAT_UI
  );

  return (
    !quote.isOrdered &&
    quote.quoteStatus === QuoteStatus.APPROVED &&
    isDateInThePast(expirationDate)
  );
};

export const hasOutdatedDiscount = ({
  quote,
  quoteItem,
  productList,
}: {
  quote: CurrentQuoteState;
  quoteItem: LineItem;
  productList: ProductData[];
}) => {
  const isQuotePending = quote.quoteStatus === QuoteStatus.PENDING;
  const isQuotePendingRevision = quote.quoteStatus === QuoteStatus.REVISION_PENDING;

  if (!isQuotePending && !isQuotePendingRevision) {
    return false;
  }

  const product = productList.find((item) => item.partNumber === quoteItem.partNumber);

  if (!product || product.priceLine === QUOTE_ITEM_EXTENDED_WARRANTY_LINE) {
    return false;
  }

  return product.sellPrice !== quoteItem.sellPrice;
};

const isDateInThePast = (date: string) => {
  let expired;
  // Wrap in try/catch to handle errors
  if (date) {
    try {
      expired = moment()
        .utc()
        .isAfter(moment(date + ' -07:00', 'MM/DD/YYYY HH:mm:ss Z').utc());
    } catch (e) {
      expired = false;
    }
  }
  return expired;
};

export const isLockedByCurrentUser = ({
  email,
  lockedBy,
  lockExpiry,
}: {
  email: string;
  lockedBy: string;
  lockExpiry: string;
}) => {
  return lockedBy === email && !isDateInThePast(lockExpiry);
};

export const isQuoteLockedByAnotherUser = ({
  email,
  lockedBy,
  lockExpiry,
}: {
  email: string;
  lockedBy: string;
  lockExpiry: string;
}) => {
  return lockedBy !== email && lockedBy !== '#unlocked#' && !isDateInThePast(lockExpiry);
};

export const isQuoteUnlocked = ({
  lockedBy,
  lockExpiry,
}: {
  lockedBy: string;
  lockExpiry: string;
}) => {
  return lockedBy === '#unlocked#' || isDateInThePast(lockExpiry);
};

export const hasWritePermission = (permissions: string[]) => {
  return permissions && permissions.includes('Write');
};

export const calculateQuoteValidationState = ({
  quoteData,
  quoteValidation,
  account,
  authenticationInformation,
  loggedInAccount,
}: {
  quoteData: CurrentQuoteState;
  quoteValidation: CurrentQuoteValidationState;
  account;
  authenticationInformation;
  loggedInAccount;
}) => {
  if (!quoteData || !Object.keys(quoteData).length) {
    return defaultCurrentQuoteValidationState;
  }

  const { email } = authenticationInformation;
  const { transactionId, lockedBy, lockExpiry, items = [], modified } = quoteData || {};

  const isNewQuote = transactionId === '';

  // Some items like the Warranty Configurator don't have a part number and can be ignored
  const activeItems = items.filter((item) => item.partNumber && item.status !== ItemStatus.REMOVED);

  const hasOutdatedDiscounts = activeItems.some((item) => item.discountOutdated);
  const hasUnavailableItems = activeItems.some(
    (item) => !item.available && item.available !== undefined
  );
  const isLockedByAnotherUser =
    isQuoteLockedByAnotherUser({ lockedBy, lockExpiry, email }) && !isNewQuote;
  const hasOneTimeDiscounts =
    quoteData.calculatedOneTimeDiscount > 0 || activeItems.some((item) => item.oneTimeDiscount);
  const hasItemsWithQuantityZero = activeItems.some((item) => item.quantity === 0);
  const hasInvalidItemQuantity = itemsHaveInvalidItemQuantity(activeItems);
  const hasWarrantyEligibleItems = activeItems.some((item) => item.extendedWarrantyEligible);
  const isEmpty = !activeItems.some((item) => item.status !== ItemStatus.REMOVED);
  const hasCurrencyMismatch = hasCurrencyMismatchBetweenQuoteAndAccount({
    authenticationInformation,
    selectedAccount: account,
    loggedInAccount,
    currentQuote: quoteData,
  });
  const hasShipToCountry = Boolean(quoteData.shippingAddress.country);
  const hasEcommercePermission =
    hasWritePermission(quoteData.eCommercePermissions) || quoteData.isOrdered || isNewQuote;

  const allItemsQuotable = !activeItems.some((lineItem) => !lineItem.discountType);
  const isExpired = isQuoteExpired({ quote: quoteData });
  const isWritable =
    hasEcommercePermission &&
    !(hasOutdatedDiscounts || hasUnavailableItems || isLockedByAnotherUser || hasCurrencyMismatch);

  return {
    ...quoteValidation,
    allItemsQuotable,
    isApproved: quoteData.quoteStatus === QuoteStatus.APPROVED,
    hasWritePermission: hasEcommercePermission,
    isExpired,
    isCheckoutAllowed: isCheckoutAllowed({
      quote: quoteData,
      isExpired,
      isWritable,
      isLockedByAnotherUser,
      hasCurrencyMismatch,
      hasOutdatedDiscounts,
      hasUnavailableItems,
      hasActiveItems: !isEmpty,
    }),
    isWritable,
    hasOutdatedDiscounts,
    hasUnavailableItems,
    hasOneTimeDiscounts,
    hasInvalidItemQuantity,
    hasItemsWithQuantityZero,
    hasWarrantyEligibleItems,
    hasCurrencyMismatch,
    isLockedByAnotherUser,
    hasShipToCountry,
    isEmpty,
    isLockedByCurrentUser: isLockedByCurrentUser({ email, lockedBy, lockExpiry }),
    isModified: modified,
  };
};
